(() => {
  if (customElements.get('sac-bc-coilover-finder')) return;
  const products = new Map();
  const encodeQuery = query => encodeURIComponent(query.replace(/([^\s-])(-+)/g, '$1$2-').replace(/([^\s-]) ([^\s-])/g, '$1-$2'));
  const lookupFor = value => value.trim().replace(/\s+/g, ' ').slice(0, 160);
  const isBc = value => typeof value === 'string' && value.trim().toLowerCase() === 'bc racing';

  class SacBcCoiloverFinder extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.form = this.querySelector('[data-bc-form]');
      this.input = this.querySelector('[data-bc-input]');
      this.results = this.querySelector('[data-bc-results]');
      this.featured = this.querySelector('[data-bc-featured]');
      this.status = this.querySelector('[data-bc-status]');
      this.clearButton = this.querySelector('[data-bc-clear]');
      this.viewAll = this.querySelector('[data-bc-view-all]');
      this.viewLabel = this.querySelector('[data-bc-view-label]');
      this.baseViewLabel = this.baseViewLabel || this.viewLabel?.textContent || '';
      if (!this.form || !this.input || !this.results || !this.status) return;
      this.onInput = () => this.update(false);
      this.onSubmit = event => {
        event.preventDefault();
        window.location.assign(this.destination(lookupFor(this.input.value)));
      };
      this.onViewAll = () => { this.viewAll.href = this.destination(lookupFor(this.input.value)); };
      this.onClick = event => {
        const chip = event.target.closest('[data-bc-query]');
        if (chip && this.contains(chip)) {
          if (event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.input.value = chip.dataset.bcQuery;
          this.update(true);
          this.input.focus({ preventScroll: true });
        }
        if (event.target.closest('[data-bc-clear]')) this.clear();
      };
      this.onKeydown = event => {
        if (event.key === 'ArrowDown' && event.target === this.input) {
          const first = this.results.querySelector('a[href]');
          if (first) {
            event.preventDefault();
            first.focus();
          }
        }
        if (event.key === 'Escape' && (this.input.value || !this.results.hidden)) {
          event.preventDefault();
          this.clear();
        }
      };
      this.input.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);
      this.viewAll?.addEventListener('click', this.onViewAll);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown, true);
      this.update(false);
    }

    vehicle() {
      try {
        const app = window.Convermax;
        if (app?.isVehicleSelected?.() && app.getVehicleRequestParams) {
          const params = app.getVehicleRequestParams();
          if (typeof params === 'string') return params;
        }
      } catch {}
      return '';
    }

    destination(query) {
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.search = `?vendor/BC-Racing${query ? `&query=${encodeQuery(query)}` : ''}`;
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview && /^\d+$/.test(preview)) url.search += `&preview_theme_id=${preview}`;
      return url.href;
    }

    endpoint(query, vehicle) {
      const url = new URL(this.dataset.suggestUrl);
      url.searchParams.set('query', query);
      url.searchParams.set('pagesize', '6');
      url.searchParams.set('page', '0');
      url.searchParams.set('scope', 'Products');
      for (const key of ['country', 'currency', 'locale']) if (this.dataset[key]) url.searchParams.set(`extra.${key}`, this.dataset[key]);
      const groups = new Map();
      for (const [key, value] of new URLSearchParams(vehicle)) {
        const match = key.match(/^facet\.(\d+)\.(field|selection)$/);
        if (!match) continue;
        const group = groups.get(match[1]) || { selections: [] };
        if (match[2] === 'field') group.field = value;
        else group.selections.push(value);
        groups.set(match[1], group);
      }
      let index = 0;
      for (const { field, selections } of groups.values()) {
        if (!['Year', 'Make', 'Model', 'Submodel'].includes(field) || !selections.length) continue;
        url.searchParams.set(`facet.${index}.field`, field);
        selections.forEach(value => url.searchParams.append(`facet.${index}.selection`, value));
        index += 1;
      }
      url.searchParams.set(`facet.${index}.field`, 'vendor');
      url.searchParams.set(`facet.${index}.selection`, 'BC Racing');
      return url;
    }

    clear() {
      this.input.value = '';
      this.update(false);
      this.input.focus({ preventScroll: true });
    }

    cancel() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.sequence = (this.sequence || 0) + 1;
      this.results?.removeAttribute('aria-busy');
    }

    update(immediate) {
      this.cancel();
      const raw = this.input.value.trim(), lookup = lookupFor(raw), vehicle = this.vehicle();
      this.results.replaceChildren();
      this.results.hidden = true;
      if (this.featured) this.featured.hidden = false;
      if (this.clearButton) this.clearButton.hidden = !raw;
      if (this.viewAll) this.viewAll.hidden = true;
      if (this.viewLabel) this.viewLabel.textContent = this.baseViewLabel;
      this.status.textContent = raw && lookup.length < 2 ? this.dataset.prompt : '';
      this.querySelectorAll('[data-bc-query]').forEach(chip => {
        if (lookup && lookupFor(chip.dataset.bcQuery) === lookup) chip.setAttribute('aria-current', 'true');
        else chip.removeAttribute('aria-current');
      });
      if (lookup.length < 2) return;
      if (this.viewAll) {
        this.viewAll.href = this.destination(lookup);
        this.viewAll.hidden = false;
      }
      const sequence = this.sequence;
      if (immediate) this.search(raw, lookup, sequence, vehicle);
      else this.timer = setTimeout(() => this.search(raw, lookup, sequence, vehicle), 250);
    }

    current(raw, sequence, vehicle) {
      return this.isConnected && sequence === this.sequence && this.input.value.trim() === raw && this.vehicle() === vehicle;
    }

    productLink(value) {
      try {
        const url = new URL(value, window.location.origin);
        const match = url.pathname.match(/\/products\/([a-z0-9][a-z0-9-]*)\/?$/i);
        if (url.origin === window.location.origin && !url.username && !url.password && match) return { url, handle: match[1] };
      } catch {}
      return null;
    }

    async product(item, handle, signal) {
      const key = `${this.dataset.rootUrl}:${this.dataset.currency}:${this.dataset.country}:${handle}`;
      let cached = products.get(key);
      if (!cached || Date.now() - cached.time > 60000) {
        const endpoint = new URL(`${(this.dataset.rootUrl || '/').replace(/\/+$/, '')}/products/${handle}.js`, window.location.origin);
        const response = await fetch(endpoint, { signal });
        if (!response.ok) throw new Error('Product unavailable');
        cached = { value: await response.json(), time: Date.now() };
      }
      const product = cached.value, id = String(item.id);
      if (!/^[1-9]\d*$/.test(id) || String(product.id) !== id || !isBc(product.vendor) || !Number.isInteger(product.price_min) || !Number.isInteger(product.price_max) || product.price_min < 0 || product.price_max < product.price_min) return null;
      products.delete(key);
      products.set(key, cached);
      if (products.size > 30) products.delete(products.keys().next().value);
      return product;
    }

    money(cents) {
      return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
    }

    card(item, link, product) {
      const row = document.createElement('li');
      row.className = 'sac-bc-card';
      row.setAttribute('data-bc-card', '');
      row.dataset.productId = String(item.id);
      const title = String(item.title || '').replace(/<\/?b>/g, '');
      try {
        const imageUrl = new URL(item.image, window.location.origin);
        if (typeof item.image === 'string' && /^https?:$/.test(imageUrl.protocol) && !imageUrl.username && !imageUrl.password) {
          const imageLink = document.createElement('a'), image = document.createElement('img');
          imageLink.className = 'sac-bc-card__image';
          imageLink.href = link.url.href;
          imageLink.setAttribute('aria-label', title);
          image.src = imageUrl.href;
          image.alt = title;
          image.width = image.height = 450;
          image.loading = 'lazy';
          imageLink.append(image);
          row.append(imageLink);
        }
      } catch {}
      const body = document.createElement('div'), brand = document.createElement('p'), heading = document.createElement('h3'), anchor = document.createElement('a');
      body.className = 'sac-bc-card__body';
      brand.className = 'sac-bc-card__brand';
      brand.textContent = this.dataset.brandLabel;
      anchor.href = link.url.href;
      anchor.textContent = title;
      heading.append(anchor);
      body.append(brand, heading);
      if (product) {
        const price = document.createElement('p');
        price.className = 'sac-bc-card__price';
        price.textContent = product.price_min !== product.price_max ? `${this.dataset.fromPrice || ''} ${this.money(product.price_min)}`.trim() : this.money(product.price_min);
        body.append(price);
        const badge = this.querySelector('[data-entry-badge-template]')?.content.querySelector('sac-entry-badge')?.cloneNode(true);
        if (badge) {
          Object.assign(badge.dataset, { productId: String(product.id), priceMin: String(product.price_min), priceMax: String(product.price_max) });
          body.append(badge);
        }
        const variants = Array.isArray(product.variants) ? product.variants.filter(variant => /^[1-9]\d*$/.test(String(variant.id)) && typeof variant.title === 'string' && Number.isInteger(variant.price) && variant.price >= 0).slice(0, 50) : [];
        if (variants.length > 1 || (variants.length === 1 && variants[0].title !== 'Default Title')) {
          const details = document.createElement('details'), summary = document.createElement('summary'), list = document.createElement('ul');
          details.className = 'sac-bc-card__options';
          summary.textContent = this.dataset.optionsLabel;
          list.setAttribute('role', 'list');
          variants.forEach(variant => {
            const option = document.createElement('li'), action = document.createElement('a'), text = document.createElement('span'), price = document.createElement('span');
            const url = new URL(link.url.href);
            url.searchParams.set('variant', String(variant.id));
            action.href = url.href;
            text.textContent = variant.title;
            price.className = 'sac-bc-card__variant-price';
            price.textContent = this.money(variant.price);
            action.append(text, price);
            option.append(action);
            list.append(option);
          });
          details.append(summary, list);
          body.append(details);
        }
      }
      const action = document.createElement('a'), arrow = document.createElement('span');
      action.className = 'sac-bc-card__action';
      action.href = link.url.href;
      action.textContent = this.dataset.actionLabel;
      action.setAttribute('aria-label', `${this.dataset.actionLabel}: ${title}`);
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '↗';
      action.append(arrow);
      body.append(action);
      row.append(body);
      return row;
    }

    async search(raw, lookup, sequence, vehicle) {
      if (!this.current(raw, sequence, vehicle)) return;
      const controller = this.controller = new AbortController();
      const { signal } = controller, timeout = setTimeout(() => controller.abort(), 12000);
      this.status.textContent = this.dataset.loading;
      this.results.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.endpoint(lookup, vehicle), { signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        const states = String(data.State).toLowerCase().split(',').map(value => value.trim());
        if (!Array.isArray(data.Items) || !states.every(value => ['normal', 'corrected', 'phonetic', 'partial', 'nothing'].includes(value))) throw new Error('Search response unavailable');
        if (!this.current(raw, sequence, vehicle)) return;
        const seen = new Set();
        const matches = data.Items.map(item => ({ item, link: this.productLink(item.url) })).filter(({ item, link }) => {
          const id = String(item.id);
          if (!link || !isBc(item.vendor) || !/^[1-9]\d*$/.test(id) || seen.has(id)) return false;
          seen.add(id);
          return true;
        }).slice(0, 6);
        const rows = await Promise.all(matches.map(async ({ item, link }) => this.card(item, link, await this.product(item, link.handle, signal).catch(() => null))));
        if (!this.current(raw, sequence, vehicle)) return;
        const grid = document.createElement('ul');
        grid.className = 'sac-bc__grid';
        grid.setAttribute('role', 'list');
        grid.append(...rows);
        this.results.replaceChildren(grid);
        this.results.hidden = !rows.length;
        if (this.featured) this.featured.hidden = true;
        const total = Number.isInteger(data.TotalHits) && data.TotalHits >= rows.length ? data.TotalHits : rows.length;
        this.status.textContent = rows.length
          ? this.dataset.resultsLabel.replace('__COUNT__', String(total)).replace('__QUERY__', lookup)
          : this.dataset.empty;
        if (this.viewLabel) this.viewLabel.textContent = `${this.baseViewLabel} (${total})`;
      } catch {
        if (this.current(raw, sequence, vehicle)) this.status.textContent = this.dataset.error;
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }

    disconnectedCallback() {
      this.cancel();
      this.input?.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.viewAll?.removeEventListener('click', this.onViewAll);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown, true);
    }
  }
  customElements.define('sac-bc-coilover-finder', SacBcCoiloverFinder);
})();
